import React, { useEffect } from "react";
import { useMount, usePrevious, useUnmount } from "react-use";

import { useAnalytics } from "lib/contexts/analytics";
import { useSession } from "links/lib/contexts/session";
import { useSessionActions } from "links/lib/contexts/sessionActions";
import { useSessionGameState } from "links/lib/contexts/sessionGameState";
import {
  AnalyticsEvent,
  IUser,
  SessionGameType,
  WebSocketSession,
} from "links/lib/types";
import { useStudent } from "sessionComponents/contexts/student";
import { StudentLobbyLight as TheStudentLobbyLight } from "sessionComponents/organisms/StudentLobbyLight";
import { useEditAvatar } from "sharedComponents/hooks/useEditAvatar";

export const StudentLobbyLight: React.FC<{
  authUser: IUser;
  refetchUser: () => void;
  enableUnloadWarning: () => void;
  disableUnloadWarning: () => void;
}> = ({ authUser, refetchUser, enableUnloadWarning, disableUnloadWarning }) => {
  const session = useSession() as WebSocketSession;
  const { avatarUpdated, guestSignedIn, shuffleGuestName } =
    useSessionActions();
  const student = useStudent();
  const gameState = useSessionGameState();
  const { trackEvent } = useAnalytics();

  // Check for guest users signing in to existing accounts. In this
  // case, we need to instruct the session to replace the guest user with
  // the true user. In the event that a guest user signs in, but doesn't
  // have an existing account, the guest user will be upgraded and we
  // can continue using the same user object.
  const prevStudent = usePrevious(student);
  useEffect(() => {
    if (
      prevStudent &&
      prevStudent.is_guest &&
      prevStudent.id !== student.id &&
      !student.is_guest
    ) {
      guestSignedIn(prevStudent.id);
    }
  }, [guestSignedIn, prevStudent, student.id, student.is_guest]);

  useMount(() => {
    disableUnloadWarning();
  });

  useUnmount(() => {
    enableUnloadWarning();
  });

  const {
    modal: editAvatarModal,
    profileImageCapture,
    onOpen: onOpenEditAvatarModal,
  } = useEditAvatar({
    useModal: true,
    modalSize: "6xl",
    refetchAvatarOnWindowFocus: true,
    onClose: () => avatarUpdated(),
    onDone: () => avatarUpdated(),
    onEquipAvatarItem: (avatarItemId, categoryId, colorHex) => {
      trackEvent(AnalyticsEvent.Session_StudentLobby_EditAvatar_EquipItem, {
        itemId: avatarItemId,
        itemCategoryId: categoryId,
        itemColorHexCode: colorHex,
      });
    },
    onDequipAvatarItem: (avatarItemId, categoryId) => {
      if (!categoryId) return;

      trackEvent(AnalyticsEvent.Session_StudentLobby_EditAvatar_UnsetCategory, {
        itemCategoryId: categoryId,
      });
    },
    onSetSkeletonType: (skeletonType) => {
      trackEvent(
        AnalyticsEvent.Session_StudentLobby_EditAvatar_SetSkeletonType,
        {
          skeletonType: skeletonType,
        }
      );
    },
    onSetHairColor: (hairColorHexCode) => {
      trackEvent(AnalyticsEvent.Session_StudentLobby_EditAvatar_SetHairColor, {
        colorHexCode: hairColorHexCode,
      });
    },
    onSetSkinTone: (skinToneHexCode) => {
      trackEvent(AnalyticsEvent.Session_StudentLobby_EditAvatar_SetSkinTone, {
        toneHexCode: skinToneHexCode,
      });
    },
  });

  return (
    <>
      <TheStudentLobbyLight
        authUser={authUser}
        outerGame={gameState?.game_type || SessionGameType.QuickPlay}
        joinCode={session.code}
        student={student}
        onOpenEditAvatarModal={onOpenEditAvatarModal}
        onShuffleGuestName={shuffleGuestName}
        onSignInSuccess={refetchUser}
        sessionCreatorName={session.creator_name}
      />
      {editAvatarModal}
      {profileImageCapture}
    </>
  );
};
